import { useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import ReactToPrint from "react-to-print";

//internal imports
import { FtextField } from "../../../components/common/FtextField";
import "../../Customer/customer.css";
import { createCustomerInvoice } from "../../../features/apiCalls";
import Loader from "../../../components/common/Loader";
import ComponentCustomModal from "../../../components/common/customModal/ComponentCustomModal";
import useISPowner from "../../../hooks/useISPOwner";
import CustomerInvoicePrint from "../../customerInvoice/customerInvoicePrint/CustomerInvoicePrint";
import { CashStack } from "react-bootstrap-icons";
import customerBillMonth from "./customerBillMonth";

const CreateInvoice = ({ show, setShow, single, customerData }) => {
  const { t } = useTranslation();

  const options = [
    { value: "January", label: t("january") },
    { value: "February", label: t("february") },
    { value: "March", label: t("march") },
    { value: "April", label: t("april") },
    { value: "May", label: t("may") },
    { value: "June", label: t("june") },
    { value: "July", label: t("July") },
    { value: "August", label: t("august") },
    { value: "September", label: t("september") },
    { value: "October", label: t("october") },
    { value: "November", label: t("november") },
    { value: "December", label: t("december") },
  ];

  //---> Get user & current user data form useISPOwner hooks
  const {
    role,
    ispOwnerId,
    ispOwnerData,
    bpSettings,
    userData,
    currentUser,
    permissions,
  } = useISPowner();

  // get all customer
  const customer = useSelector((state) => state?.customer?.customer);

  // find editable data
  const data = customer.find((item) => item.id === single);

  const [isLoading, setLoading] = useState(false);

  //billing date
  const [startDate, setStartDate] = useState(false);
  const [endDate, setEndDate] = useState(false);
  const [medium, setMedium] = useState("cash");
  const [noteCheck, setNoteCheck] = useState(false);
  const [note, setNote] = useState("");
  const [selectedMonth, setSelectedMonth] = useState([]);
  const [billAmount, setBillAmount] = useState();
  const [balanceDue, setBalanceDue] = useState();
  const [billType, setBillType] = useState("bill");
  const [amount, setAmount] = useState(null);
  const totalAmount = Number(billAmount) + Number(balanceDue);
  const maxDiscount = totalAmount;

  //response data after API call after payment
  const [responseData, setResponseData] = useState({});
  const [test, setTest] = useState(false);

  const rechargePrint = useRef();

  //print button is clicked after successful response
  useEffect(() => {
    if (test) {
      if (
        (role === "ispOwner" && bpSettings?.instantRechargeBillPrint) ||
        ((role === "manager" || role === "collector") &&
          permissions?.instantRechargeBillPrint &&
          bpSettings?.instantRechargeBillPrint)
      ) {
        document.getElementById("printButton").click();
        setTest(!test);
      }
    }
  }, [test]);

  //Validation
  const BillValidatoin = Yup.object({
    amount: Yup.number()
      .min(Math.floor(data?.monthlyFee / 3), t("billNotAcceptable"))
      .integer(t("decimalNumberNotAcceptable")),
    due: Yup.number()
      .min(0, t("dueNotAcceptable"))
      .max(
        data?.balance < 0 ? Math.abs(data?.balance) : 0,
        t("dueNotAcceptable"),
      )
      .integer(t("decimalNumberNotAcceptable")),
    discount: Yup.number()
      .min(0, t("discountNotAcceptable"))
      .max(maxDiscount, t("discountNotAcceptable"))
      .integer(t("decimalNumberNotAcceptable")),
  });

  //form resetFunction
  const resetForm = () => {
    setStartDate(false);
    setEndDate(false);
    setNote("");
    setNoteCheck(false);
  };

  // set customer bill amount & due
  useEffect(() => {
    setBalanceDue(data?.balance < 0 ? Math.abs(data?.balance) : 0);
    setBillAmount(
      data?.balance > 0 && data?.balance <= data?.monthlyFee
        ? data?.monthlyFee - data?.balance
        : data?.monthlyFee,
    );

    // set customer bill month
    setSelectedMonth(customerBillMonth(data));
  }, [data]);

  const handleFormValue = (event) => {
    if (event.target.name === "amount") {
      setBillAmount(event.target.value);
    }
    if (event.target.name === "due") {
      setBalanceDue(event.target.value);
    }
  };

  // bill amount
  const customerBillHandler = (formValue) => {
    if (balanceDue > (data?.balance < 0 ? Math.abs(data?.balance) : 0)) {
      setLoading(false);
      return alert(t("dueNotAcceptable"));
    }

    if (balanceDue < 0) {
      setLoading(false);
      return alert(t("dueNotAcceptable"));
    }
    if (maxDiscount < formValue.discount) {
      setLoading(false);
      return alert(t("discountNotAcceptable"));
    }

    const sendingData = {
      amount: formValue.amount + formValue.due,
      discount: formValue.discount,
      name: userData.name,
      collectedBy: currentUser?.user.role,
      billType: billType,
      customer: data?.id,
      ispOwner: ispOwnerId,
      user: currentUser?.user.id,
      collectorId: userData?.id, //when collector is logged in
      medium,
      package: data?.pppoe.profile,
      invoiceType: "customerInvoice",
    };

    if (note) sendingData.note = note;

    if (startDate && endDate) {
      sendingData.start = startDate.toISOString();
      sendingData.end = endDate.toISOString();
    }
    if (selectedMonth?.length > 0) {
      const monthValues = selectedMonth.map((item) => {
        return item.value;
      });
      sendingData.month = monthValues.join(",");
    }

    createCustomerInvoice(sendingData, setLoading, resetForm, setShow);

    setAmount(data.amount);
  };

  return (
    <>
      <ComponentCustomModal
        show={show}
        setShow={setShow}
        centered={false}
        size={"md"}
        header={t("createInvoice")}
      >
        <div>
          <table
            className="table table-bordered"
            style={{ lineHeight: "12px" }}
          >
            <tbody>
              <tr>
                <td>{t("id")}</td>
                <td>
                  <b>{customerData?.customerId}</b>
                </td>
                <td>{t("pppoe")}</td>
                <td>
                  <b>{customerData?.pppoe.name}</b>
                </td>
              </tr>
              <tr>
                <td>{t("name")}</td>
                <td>
                  <b>{customerData?.name}</b>
                </td>
                <td>{t("mobile")}</td>
                <td className="text-primary">
                  <b>{customerData?.mobile}</b>
                </td>
              </tr>
              <tr>
                <td>{t("monthly")}</td>
                <td className="text-primary">
                  <b>{customerData?.monthlyFee}</b>
                </td>
                <td>{t("balance")}</td>
                <td
                  className={
                    customerData?.balance < 0 ? "text-danger" : "text-success"
                  }
                >
                  <b>{customerData?.balance}</b>
                </td>
              </tr>
            </tbody>
          </table>

          <Formik
            initialValues={{
              billingType: "monthly",
              amount: billType === "bill" ? billAmount : totalAmount,
              due: balanceDue,
              discount: 0,
            }}
            validationSchema={BillValidatoin}
            onSubmit={(values) => {
              customerBillHandler(values);
            }}
            enableReinitialize
          >
            {({ setFieldValue }) => (
              <Form onChange={(event) => handleFormValue(event, setFieldValue)}>
                <div className="monthlyBill">
                  <span className="text-secondary">{data?.name}</span>
                  &nbsp;
                  <span className="text-secondary">{t("totalAmount")} ৳</span>
                  <span className="text-primary">{totalAmount} </span>
                </div>

                <div className="displayGrid">
                  <div className="displayGrid2">
                    <div>
                      <label className="form-control-label changeLabelFontColor">
                        {t("billType")}
                        <span className="text-danger mx-1">*</span>
                      </label>
                      <select className="form-select mt-0 mw-100" disabled>
                        <option value="bill"> {t("bill")} </option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="billingType_type"
                        className="form-control-label changeLabelFontColor"
                      >
                        {t("billingType")}
                        <span className="text-danger mx-1">*</span>
                      </label>

                      <select
                        as="select"
                        name="billingType"
                        className="form-select mt-0 mw-100"
                        disabled
                      >
                        <option value="monthly"> {t("monthly")} </option>
                      </select>
                    </div>

                    <FtextField type="number" name="amount" label={t("bill")} />

                    <FtextField type="number" name="due" label={t("due")} />

                    <div>
                      <label
                        htmlFor="receiver_type"
                        className="form-control-label changeLabelFontColor"
                      >
                        {t("medium")}{" "}
                        <span className="text-danger mx-1">*</span>
                      </label>

                      <select
                        as="select"
                        id="receiver_type"
                        className="form-select mt-0 mw-100"
                        aria-label="Default select example"
                        onChange={(e) => setMedium(e.target.value)}
                      >
                        <option value="cash" selected>
                          {t("handCash")}
                        </option>
                        <option value="bKash"> {t("bKash")} </option>
                        <option value="rocket"> {t("rocket")} </option>
                        <option value="nagad"> {t("nagad")} </option>
                        <option value="others"> {t("others")} </option>
                      </select>
                    </div>
                  </div>

                  <div className="month">
                    <label
                      className="form-check-label changeLabelFontColor"
                      htmlFor="selectMonth"
                    >
                      {t("selectMonth")}
                    </label>

                    <Select
                      className="mt-0"
                      name="month"
                      value={selectedMonth}
                      onChange={(data) => setSelectedMonth(data)}
                      options={options}
                      isMulti={true}
                      placeholder={t("selectMonth")}
                      isSearchable
                      id="selectMonth"
                    />
                  </div>

                  <div className="displayGrid2">
                    {(role === "ispOwner" || permissions.billDiscount) && (
                      <div>
                        <FtextField
                          type="number"
                          name="discount"
                          label={t("discount")}
                        />
                      </div>
                    )}

                    <div className="d-flex align-self-end">
                      <input
                        type="checkbox"
                        className="form-check-input me-1"
                        id="addnote"
                        checked={noteCheck}
                        onChange={(e) => setNoteCheck(e.target.checked)}
                      />
                      <label
                        className="form-check-label changeLabelFontColor"
                        htmlFor="addnote"
                      >
                        {t("noteAndDate")}
                      </label>
                    </div>
                  </div>

                  {noteCheck && (
                    <>
                      <div className="displayGrid2">
                        <div>
                          <label className="form-control-label changeLabelFontColor">
                            {t("startDate")}
                          </label>
                          <DatePicker
                            className="form-control mw-100"
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={t("selectDate")}
                          />
                        </div>

                        <div>
                          <label className="form-control-label changeLabelFontColor">
                            {t("endDate")}
                          </label>

                          <DatePicker
                            className="form-control mw-100"
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={t("selectDate")}
                          />
                        </div>
                      </div>

                      <div class="form-floating">
                        <textarea
                          cols={200}
                          className="form-control shadow-none"
                          placeholder={t("writeNote")}
                          id="noteField"
                          onChange={(e) => setNote(e.target.value)}
                        ></textarea>
                        <label htmlFor="noteField"> {t("addNote")} </label>
                      </div>
                    </>
                  )}
                </div>

                {/* Invoice Printer Page Component with button and they are hidden*/}
                <>
                  {((role === "ispOwner" &&
                    bpSettings?.instantRechargeBillPrint) ||
                    ((role === "manager" || role === "collector") &&
                      permissions?.instantRechargeBillPrint &&
                      bpSettings?.instantRechargeBillPrint)) && (
                    <div className="d-none">
                      <CustomerInvoicePrint
                        ref={rechargePrint}
                        invoiceData={{
                          ...responseData,
                          customer: data,
                          status: "both",
                        }}
                        ispOwnerData={ispOwnerData}
                      />
                    </div>
                  )}

                  <div className="d-none">
                    <ReactToPrint
                      trigger={() => (
                        <div
                          title={t("printInvoiceBill")}
                          style={{ cursor: "pointer" }}
                        >
                          <button type="button" id="printButton">
                            Print
                          </button>
                        </div>
                      )}
                      content={() => rechargePrint.current}
                    />
                  </div>
                </>

                <div className="d-flex justify-content-end mt-4">
                  <button type="submit" className="btn btn-outline-success">
                    {isLoading ? (
                      <Loader />
                    ) : (
                      <span className="submitButton">
                        {t("pay")}
                        <CashStack />
                      </span>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </ComponentCustomModal>
    </>
  );
};

export default CreateInvoice;
